import { format } from 'date-fns';
import { Code2, LockKeyhole } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export type CommitActivityDay = {
  key: string;
  date: Date;
  count: number;
};

export type DeveloperOverviewHeaderProps = {
  developerUnlocked: boolean;
  canViewCommitData: boolean;
  isAdminUser: boolean;

  commitsCount: number;
  ratePerCommit: number;
  totalBilled: number;
  totalUnbilled: number;
  retainerPaid: number;
  totalCommitCost: number;

  isFirebaseConfigured: boolean;
  dataSourceLabel: string;
  dataSource: 'firestore' | 'local' | 'github';

  commitCalendarDays: CommitActivityDay[];
  commitActivityLabel?: string;

  onOpenDeveloperDialog: () => void;
  onLockView: () => void;
};

export function DeveloperOverviewHeader({
  developerUnlocked,
  canViewCommitData,
  isAdminUser,
  commitsCount,
  ratePerCommit,
  totalBilled,
  totalUnbilled,
  retainerPaid,
  totalCommitCost,
  isFirebaseConfigured,
  dataSourceLabel,
  dataSource,
  commitCalendarDays,
  commitActivityLabel,
  onOpenDeveloperDialog,
  onLockView,
}: DeveloperOverviewHeaderProps) {
  return (
    <>
      {/* Developer Info Card */}
      <Card className="mb-8 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <Code2 className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-xl sm:text-2xl font-display">Mysttech Creations</CardTitle>
                <CardDescription className="text-sm sm:text-base">Lead Developer · THIRTEEN Galactic Calendar</CardDescription>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              <Badge variant="outline" className="text-primary border-primary/50 self-start sm:self-auto">
                Active Developer
              </Badge>
              <div className="flex flex-col items-start sm:items-end gap-2">
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto gap-2"
                    onClick={onOpenDeveloperDialog}
                  >
                    <LockKeyhole className="w-4 h-4" />
                    Developer Dialog
                  </Button>
                  {developerUnlocked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full sm:w-auto gap-2"
                      onClick={onLockView}
                    >
                      <LockKeyhole className="w-4 h-4" />
                      Lock view
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground text-left sm:text-right">
                  Click the lock to open, then enter the 6-digit passcode.
                </p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className={`grid grid-cols-1 ${canViewCommitData ? 'md:grid-cols-7' : 'md:grid-cols-4'} gap-4`}>
            {canViewCommitData && (
              <div className="bg-card/50 rounded-lg p-4 border border-border/50">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Billable Commits</p>
                <p className="text-2xl font-bold text-foreground">{commitsCount}</p>
              </div>
            )}
            <div className="bg-card/50 rounded-lg p-4 border border-border/50">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Rate per Commit</p>
              <p className="text-2xl font-bold text-foreground">${ratePerCommit}</p>
            </div>
            <div className="bg-card/50 rounded-lg p-4 border border-border/50">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Billed</p>
              <p className="text-2xl font-bold text-green-500">${totalBilled}</p>
            </div>
            <div className="bg-card/50 rounded-lg p-4 border border-border/50">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Pending</p>
              <p className="text-2xl font-bold text-amber-500">${totalUnbilled}</p>
            </div>
            <div className="bg-card/50 rounded-lg p-4 border border-border/50">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Retainer Paid</p>
              <p className="text-2xl font-bold text-primary">${retainerPaid}</p>
            </div>
            {canViewCommitData && (
              <div className="bg-card/50 rounded-lg p-4 border border-border/50">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Total Commit Cost</p>
                <p className="text-2xl font-bold text-foreground">${totalCommitCost}</p>
              </div>
            )}
            {canViewCommitData && (
              <div className="bg-card/50 rounded-lg p-4 border border-border/50">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Firestore</p>
                <p className="text-sm font-semibold text-foreground">
                  {isFirebaseConfigured ? dataSourceLabel : 'Not Configured'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {dataSource === 'firestore'
                    ? 'Live data'
                    : dataSource === 'github'
                      ? 'Manual GitHub sync'
                      : 'Local only'}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {isAdminUser && canViewCommitData && (
        <div className="mb-8">
          <Card className="w-full">
            <CardHeader>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-base">Commit activity</CardTitle>
                  <CardDescription>{commitActivityLabel ?? 'Last 12 months'}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-hidden sm:overflow-x-auto">
                <div className="origin-left scale-[0.75] sm:scale-100 px-2 sm:px-0 py-1 sm:py-0">
                  <div className="grid grid-flow-col grid-rows-7 gap-1 min-w-max">
                    {commitCalendarDays.map((day) => {
                      let intensityClass = 'bg-muted/30';
                      if (day.count >= 6) intensityClass = 'bg-primary';
                      else if (day.count >= 3) intensityClass = 'bg-primary/60';
                      else if (day.count >= 1) intensityClass = 'bg-primary/40';
                      return (
                        <div
                          key={day.key}
                          title={`${format(day.date, 'MMM d, yyyy')}: ${day.count} commit${day.count === 1 ? '' : 's'}`}
                          className={`h-3 w-3 rounded-sm border border-border/40 ${intensityClass}`}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
}
